import React, { useEffect, useState, useRef } from 'react';
import { settingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Settings as SettingsIcon, FileText, Bell, Plus, Trash2, Save, ListChecks, X, Image, Upload
} from 'lucide-react';
import { toast } from 'sonner';

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newFormName, setNewFormName] = useState('');
  const [newFormCategory, setNewFormCategory] = useState('');
  const [newStatusName, setNewStatusName] = useState('');
  const [editingBattery, setEditingBattery] = useState(null);
  const [logo, setLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadSettings();
    loadLogo();
  }, []);

  const loadLogo = async () => {
    try {
      const response = await settingsAPI.getLogo();
      if (response.data?.logo) {
        setLogo(response.data.logo);
      }
    } catch (error) {
      // Silently fail
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 500 * 1024) {
      toast.error('Image must be less than 500KB');
      return;
    }

    setUploadingLogo(true);
    try {
      const response = await settingsAPI.uploadLogo(file);
      setLogo(response.data.logo);
      toast.success('Logo uploaded successfully');
      // Force reload the page to update header
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Remove your logo?')) return;
    
    try {
      await settingsAPI.deleteLogo();
      setLogo(null);
      toast.success('Logo removed');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (updates) => {
    setSaving(true);
    try {
      const response = await settingsAPI.update(updates);
      setSettings(response.data);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAssessmentTypeToggle = (typeName) => {
    const updated = settings.assessment_types.map(t => 
      t.name === typeName ? { ...t, active: !t.active } : t
    );
    saveSettings({ assessment_types: updated });
  };

  const handleAssessmentTypeDaysChange = (typeName, days) => {
    const updated = settings.assessment_types.map(t => 
      t.name === typeName ? { ...t, days_to_report: parseInt(days) || 30 } : t
    );
    setSettings(prev => ({ ...prev, assessment_types: updated }));
  };

  const handleBatteryChange = (typeName, formName, add) => {
    const updated = settings.assessment_types.map(t => {
      if (t.name === typeName) {
        const currentBattery = t.battery || [];
        if (add) {
          return { ...t, battery: [...currentBattery, formName] };
        } else {
          return { ...t, battery: currentBattery.filter(f => f !== formName) };
        }
      }
      return t;
    });
    setSettings(prev => ({ ...prev, assessment_types: updated }));
  };

  const handleAddAssessmentType = () => {
    if (!newTypeName.trim()) return;
    if (settings.assessment_types.some(t => t.name.toLowerCase() === newTypeName.toLowerCase())) {
      toast.error('Assessment type already exists');
      return;
    }
    const updated = [...settings.assessment_types, { name: newTypeName, days_to_report: 30, active: true, battery: [] }];
    saveSettings({ assessment_types: updated });
    setNewTypeName('');
  };

  const handleDeleteAssessmentType = async (typeName) => {
    const updated = settings.assessment_types.filter(t => t.name !== typeName);
    await saveSettings({ assessment_types: updated });
    toast.success(`"${typeName}" deleted`);
  };

  const handleFormTypeToggle = (formName) => {
    const updated = settings.form_types.map(f => 
      f.name === formName ? { ...f, active: !f.active } : f
    );
    saveSettings({ form_types: updated });
  };

  const handleAddFormType = () => {
    if (!newFormName.trim() || !newFormCategory.trim()) {
      toast.error('Name and category are required');
      return;
    }
    if (settings.form_types.some(f => f.name.toLowerCase() === newFormName.toLowerCase())) {
      toast.error('Form type already exists');
      return;
    }
    const updated = [...settings.form_types, { name: newFormName, category: newFormCategory, active: true }];
    saveSettings({ form_types: updated });
    setNewFormName('');
    setNewFormCategory('');
  };

  const handleDeleteFormType = async (formName) => {
    const updated = settings.form_types.filter(f => f.name !== formName);
    await saveSettings({ form_types: updated });
    toast.success(`"${formName}" deleted`);
  };

  // Status management
  const handleAddStatus = () => {
    if (!newStatusName.trim()) return;
    if (settings.assessment_statuses.includes(newStatusName)) {
      toast.error('Status already exists');
      return;
    }
    const updated = [...settings.assessment_statuses, newStatusName];
    saveSettings({ assessment_statuses: updated });
    setNewStatusName('');
  };

  const handleDeleteStatus = async (statusName) => {
    if (settings.assessment_statuses.length <= 3) {
      toast.error('Must have at least 3 statuses');
      return;
    }
    const updated = settings.assessment_statuses.filter(s => s !== statusName);
    await saveSettings({ assessment_statuses: updated });
    toast.success(`"${statusName}" deleted`);
  };

  const handleMoveStatus = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= settings.assessment_statuses.length) return;
    const updated = [...settings.assessment_statuses];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    saveSettings({ assessment_statuses: updated });
  };

  const handleNotificationChange = (field, value) => {
    saveSettings({ [field]: value });
  };

  const handleSaveAssessmentDays = () => {
    saveSettings({ assessment_types: settings.assessment_types });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  // Group forms by category
  const formsByCategory = settings?.form_types?.reduce((acc, form) => {
    const cat = form.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(form);
    return acc;
  }, {}) || {};

  const allFormNames = settings?.form_types?.map(f => f.name) || [];

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope']">Settings</h1>
        <p className="text-slate-500 mt-1">Configure your assessment tracker preferences</p>
      </div>

      <Tabs defaultValue="assessments" className="space-y-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="branding" data-testid="branding-tab">
            <Image className="w-4 h-4 mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="assessments" data-testid="assessments-tab">
            <SettingsIcon className="w-4 h-4 mr-2" />
            Assessment Types
          </TabsTrigger>
          <TabsTrigger value="batteries" data-testid="batteries-tab">
            <ListChecks className="w-4 h-4 mr-2" />
            Form Batteries
          </TabsTrigger>
          <TabsTrigger value="forms" data-testid="forms-tab">
            <FileText className="w-4 h-4 mr-2" />
            Form Types
          </TabsTrigger>
          <TabsTrigger value="statuses" data-testid="statuses-tab">
            <SettingsIcon className="w-4 h-4 mr-2" />
            Statuses
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="notifications-tab">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Branding</CardTitle>
              <CardDescription>
                Upload your practice logo to personalize the application.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                {/* Logo Preview */}
                <div className="w-40 h-40 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50">
                  {logo ? (
                    <img 
                      src={logo} 
                      alt="Logo preview" 
                      className="max-w-full max-h-full object-contain p-2"
                    />
                  ) : (
                    <div className="text-center text-slate-400">
                      <Image className="w-10 h-10 mx-auto mb-2" />
                      <p className="text-sm">No logo</p>
                    </div>
                  )}
                </div>

                {/* Upload Controls */}
                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="font-medium mb-2">Practice Logo</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Upload a logo to display in the header. Recommended size: 120x40 pixels. Max file size: 500KB.
                    </p>
                  </div>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/*"
                    className="hidden"
                    data-testid="logo-file-input"
                  />
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                      data-testid="upload-logo-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                    </Button>
                    
                    {logo && (
                      <Button
                        variant="outline"
                        onClick={handleDeleteLogo}
                        className="text-red-600 hover:text-red-700"
                        data-testid="delete-logo-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assessment Types Tab */}
        <TabsContent value="assessments">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Assessment Types</CardTitle>
              <CardDescription>
                Manage the types of assessments you offer. Toggle visibility and set default days to report.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings?.assessment_types?.map((type) => (
                <div 
                  key={type.name} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={type.active}
                      onCheckedChange={() => handleAssessmentTypeToggle(type.name)}
                      data-testid={`toggle-type-${type.name}`}
                    />
                    <div>
                      <p className={`font-medium ${!type.active ? 'text-slate-400' : ''}`}>
                        {type.name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-slate-500 whitespace-nowrap">Days to report:</Label>
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        value={type.days_to_report}
                        onChange={(e) => handleAssessmentTypeDaysChange(type.name, e.target.value)}
                        className="w-20"
                        data-testid={`days-${type.name}`}
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDeleteAssessmentType(type.name)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}

              <div className="flex items-center gap-2 pt-4 border-t">
                <Input
                  placeholder="New assessment type name"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddAssessmentType()}
                  data-testid="new-type-input"
                />
                <Button onClick={handleAddAssessmentType} data-testid="add-type-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={handleSaveAssessmentDays}
                  disabled={saving}
                  className="bg-slate-800 hover:bg-slate-900"
                  data-testid="save-days-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Form Batteries Tab */}
        <TabsContent value="batteries">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Form Batteries</CardTitle>
              <CardDescription>
                Configure which forms are automatically assigned when creating clients of each assessment type.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {settings?.assessment_types?.filter(t => t.active).map((type) => (
                <div key={type.name} className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-slate-900">{type.name}</h3>
                    <Badge variant="secondary">{(type.battery || []).length} forms</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(type.battery || []).map(formName => (
                      <Badge key={formName} className="bg-blue-100 text-blue-800 flex items-center gap-1">
                        {formName}
                        <button 
                          onClick={() => handleBatteryChange(type.name, formName, false)}
                          className="ml-1 hover:text-blue-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(type.battery || []).length === 0 && (
                      <span className="text-sm text-slate-500">No forms in battery</span>
                    )}
                  </div>
                  <Select onValueChange={(val) => handleBatteryChange(type.name, val, true)}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Add form to battery..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allFormNames
                        .filter(f => !(type.battery || []).includes(f))
                        .map(formName => (
                          <SelectItem key={formName} value={formName}>{formName}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              <div className="flex justify-end pt-4">
                <Button 
                  onClick={handleSaveAssessmentDays}
                  disabled={saving}
                  className="bg-slate-800 hover:bg-slate-900"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Batteries'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Form Types Tab */}
        <TabsContent value="forms">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Form Types</CardTitle>
              <CardDescription>
                Manage online forms and questionnaires. Toggle to show/hide forms from client views.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(formsByCategory).map(([category, forms]) => (
                <div key={category}>
                  <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">
                    {category}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {forms.map((form) => (
                      <div 
                        key={form.name}
                        className={`flex items-center justify-between p-3 border rounded-lg ${
                          form.active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={form.active}
                            onCheckedChange={() => handleFormTypeToggle(form.name)}
                            data-testid={`toggle-form-${form.name}`}
                          />
                          <span className={!form.active ? 'text-slate-400' : ''}>
                            {form.name}
                          </span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteFormType(form.name)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="flex items-center gap-2 pt-4 border-t">
                <Input
                  placeholder="Form name"
                  value={newFormName}
                  onChange={(e) => setNewFormName(e.target.value)}
                  className="flex-1"
                  data-testid="new-form-name-input"
                />
                <Input
                  placeholder="Category"
                  value={newFormCategory}
                  onChange={(e) => setNewFormCategory(e.target.value)}
                  className="w-40"
                  data-testid="new-form-category-input"
                />
                <Button onClick={handleAddFormType} data-testid="add-form-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Statuses Tab */}
        <TabsContent value="statuses">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Assessment Statuses</CardTitle>
              <CardDescription>
                Customize the workflow statuses for tracking assessment progress. Drag to reorder.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings?.assessment_statuses?.map((status, index) => (
                <div 
                  key={status} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-400 w-6">{index + 1}.</span>
                    <span className="font-medium">{status}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleMoveStatus(index, -1)}
                      disabled={index === 0}
                      className="h-8 w-8"
                    >
                      ↑
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleMoveStatus(index, 1)}
                      disabled={index === settings.assessment_statuses.length - 1}
                      className="h-8 w-8"
                    >
                      ↓
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDeleteStatus(status)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}

              <div className="flex items-center gap-2 pt-4 border-t">
                <Input
                  placeholder="New status name"
                  value={newStatusName}
                  onChange={(e) => setNewStatusName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddStatus()}
                  data-testid="new-status-input"
                />
                <Button onClick={handleAddStatus} data-testid="add-status-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Status
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Notification Settings</CardTitle>
              <CardDescription>
                Configure email reminders and notification preferences.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">Email Reminders</p>
                  <p className="text-sm text-slate-500">
                    Receive email reminders for upcoming and overdue reports
                  </p>
                </div>
                <Switch
                  checked={settings?.email_reminders_enabled}
                  onCheckedChange={(val) => handleNotificationChange('email_reminders_enabled', val)}
                  data-testid="email-reminders-switch"
                />
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-medium">Reminder Days Before Due</Label>
                </div>
                <p className="text-sm text-slate-500 mb-3">
                  How many days before the due date should reminders be sent?
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    value={settings?.reminder_days_before || 7}
                    onChange={(e) => handleNotificationChange('reminder_days_before', parseInt(e.target.value) || 7)}
                    className="w-20"
                    data-testid="reminder-days-input"
                  />
                  <span className="text-slate-500">days</span>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-medium">Default Days to Report</Label>
                </div>
                <p className="text-sm text-slate-500 mb-3">
                  Default number of days after assessment completion for report due date
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="90"
                    value={settings?.default_days_to_report || 30}
                    onChange={(e) => handleNotificationChange('default_days_to_report', parseInt(e.target.value) || 30)}
                    className="w-20"
                    data-testid="default-days-input"
                  />
                  <span className="text-slate-500">days</span>
                </div>
              </div>

              <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                <p className="font-medium text-amber-800">SendGrid Configuration</p>
                <p className="text-sm text-amber-700 mt-1">
                  To enable email reminders, configure your SendGrid API key in the backend environment variables 
                  (SENDGRID_API_KEY and SENDER_EMAIL).
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
